import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import Login from './components/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import Accounts from './components/Accounts';
import Cards from './pages/Cards';
import BillPayments from './components/BillPayments';
import ZelleManagement from './components/ZelleManagement';
import CryptoManagement from './components/CryptoManagement';
import TransferManagement from './components/TransferManagement';
import SupportManagement from './components/SupportManagement';
import CheckDepositsManagement from './components/CheckDepositsManagement';
import EmailCompose from './components/EmailCompose';
import './index.css';

// TEST COMPONENT TO VERIFY REACT IS WORKING
const TestComponent = () => (
  <div style={{
    position: 'fixed',
    top: '10px',
    right: '10px',
    background: 'red',
    color: 'white',
    padding: '10px',
    zIndex: 9999,
    borderRadius: '5px'
  }}>
    🚀 REACT ADMIN APP LOADED! 🚀
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route Component (redirect if already authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Placeholder components for routes not yet implemented
const AccountsPlaceholder = () => (
  <div className="text-center py-12">
    <h2 className="text-2xl font-bold text-gray-900">Accounts Management</h2>
    <p className="text-gray-600 mt-2">Coming Soon</p>
  </div>
);

const TransactionsPlaceholder = () => (
  <div className="text-center py-12">
    <h2 className="text-2xl font-bold text-gray-900">Transactions Management</h2>
    <p className="text-gray-600 mt-2">Coming Soon</p>
  </div>
);

const SupportPlaceholder = () => (
  <div className="text-center py-12">
    <h2 className="text-2xl font-bold text-gray-900">Support Management</h2>
    <p className="text-gray-600 mt-2">Coming Soon</p>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <TestComponent />
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/reset-password" 
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } 
            />

            {/* Protected Routes */}
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/users" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/accounts" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Accounts />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/cards" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <Cards />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/bill-payments" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <BillPayments />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/zelle" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <ZelleManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/crypto" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <CryptoManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/transfers" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <TransferManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />

            <Route 
              path="/check-deposits" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <CheckDepositsManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/support" 
              element={
                <ProtectedRoute>
                  <Layout>
                    <SupportManagement />
                  </Layout>
                </ProtectedRoute>
              } 
            />
            <Route
              path="/emails/compose"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmailCompose />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Catch all route - redirect to dashboard */}
            <Route 
              path="*" 
              element={<Navigate to="/" replace />} 
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
